import { useState, useEffect } from "react"
import "./leave-management.css"
import { secureApi } from "../../Config/api"
import { GET, POST } from "../../utils/apis"
import { message, DatePicker, Select, Button } from "antd"
import { CheckCircle, Clock, XCircle, List, ArrowUp, ArrowDown } from "lucide-react"
import dayjs from "dayjs"
import { useDispatch, useSelector } from "react-redux"
import { translations } from "../../utils/constants"
import { FilePdfOutlined } from "@ant-design/icons"
import axios from "axios"
import { convertItalianToLocalTime, verifyToken } from "../../utils/helpers"
import moment from "moment"
import { useHistory } from "react-router-dom";
import paths from "../../Config/paths"
import { socket } from "../../Config/socket"
const { RangePicker } = DatePicker
const { Option } = Select

const LeaveManagement = ({ language }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const user = useSelector((state) => state.authReducer?.user)

  const [loading, setLoading] = useState(false)
  const [leaves, setLeaves] = useState([])
  const [activeTab, setActiveTab] = useState("pending")
  const [expandedRows, setExpandedRows] = useState({})
  const [showConfirmModal, setShowConfirmModal] = useState(null)
  const [dateRange, setDateRange] = useState([])
  const [selectedUsers, setSelectedUsers] = useState([])
  const employeeNameList = useSelector((state) => state.employeeLogReducer.nameList)


  useEffect(() => {
    verifyToken(history, dispatch)
  }, [])

  useEffect(() => {
    const allowedRoles = ['admin', 'superAdmin']
    if (user && !allowedRoles.includes(user.role)) {
      history.push(paths?.HOME)
    }
  }, [user, history])

  const fetchLeaves = async (dates = [], users = []) => {
    try {
      setLoading(true)
      const params = {}
      if (dates?.length === 2) {
        params.start_date = dayjs(dates[0]).format("YYYY-MM-DD")
        params.end_date = dayjs(dates[1]).format("YYYY-MM-DD")
      }
      if (users?.length > 0) {
        params.employee_names = users.join(",")
      }
      params.userId = user?.id;
      const response = await secureApi?.get(GET?.GET_ALL_LEAVES, { params })
      if (response?.success) {
        setLeaves(response.data || [])
      } else {
        message.error(response?.message || translations[language]?.fetchLeavesError)
      }
    } catch (err) {
      message.error(translations[language]?.fetchLeavesError)
    } finally {
      setLoading(false)
    }
  }
  const fetchLeavesWithioutLoading = async (dates = [], users = []) => {
    try {
      setLoading(true)
      const params = {}
      if (dates?.length === 2) {
        params.start_date = dayjs(dates[0]).format("YYYY-MM-DD")
        params.end_date = dayjs(dates[1]).format("YYYY-MM-DD")
      }
      if (users?.length > 0) {
        params.employee_names = users.join(",")
      }
      params.userId = user?.id;
      const response = await secureApi?.get(GET?.GET_ALL_LEAVES, { params })
      if (response?.success) {
        setLeaves(response.data || [])
      } else {
        message.error(response?.message || translations[language]?.fetchLeavesError)
      }
    } catch (err) {
      message.error(translations[language]?.fetchLeavesError)
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateStatus = async (leaveId, status) => {
    try {
      const response = await secureApi.post(POST.UPDATE_LEAVE_STATUS, {
        leave_id: leaveId,
        status,
      })

      if (response?.success) {
        message.success(response.message)
        fetchLeaves(dateRange, selectedUsers)
      } else {
        message.error(response?.message)
      }
    } catch (err) {
      message.error(translations[language]?.updateLeaveStatusError)
    } finally {
      setShowConfirmModal(null)
    }
  }

  const toggleReason = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-GB")
  }

  const getStatusClass = (status) => {
    switch (status) {
      case "approved":
        return "status-approved"
      case "pending":
        return "status-pending"
      case "rejected":
        return "status-rejected"
      default:
        return "status-default"
    }
  }

  const getTypeClass = (type) => {
    switch (type) {
      case "vacation":
        return "type-vacation"
      case "sick":
        return "type-sick"
      case "personal":
        return "type-personal"
      default:
        return "type-default"
    }
  }

  useEffect(() => {
    fetchLeaves(dateRange, selectedUsers)
  }, [dateRange, selectedUsers])

  const filteredLeaves = leaves.filter((leave) => leave.is_approved === activeTab)

  const TrendIndicator = ({ trend, isPositive }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        color: isPositive ? "#28a745" : "#dc3545",
        fontWeight: 500,
      }}
    >
      {isPositive ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
      <span>{Math.abs(trend)}%</span>
    </div>
  )

  const dashboardCards = [
    {
      title: translations[language]?.approvedLeaves,
      value: leaves.filter((l) => l.is_approved === "approved").length,
      // icon: <CheckCircle size={22} color="#28a745" />,
      // trend: { value: 4.2, isPositive: true },
      period: translations[language]?.thisMonth,
    },
    {
      title: translations[language]?.pendingLeaves,
      value: leaves.filter((l) => l.is_approved === "pending").length,
      // icon: <Clock size={22} color="#faad14" />,
      // trend: { value: -1.5, isPositive: false },
      period: translations[language]?.thisMonth,
    },
    {
      title: translations[language]?.rejectedLeaves,
      value: leaves.filter((l) => l.is_approved === "rejected").length,
      // icon: <XCircle size={22} color="#ff4d4f" />,
      // trend: { value: 0.8, isPositive: false },
      period: translations[language]?.thisMonth,
    },
    {
      title: translations[language]?.totalRequests,
      value: leaves.length,
      // icon: <List size={22} color="#1890ff" />,
      // trend: { value: 7.3, isPositive: true },
      period: translations[language]?.thisMonth,
    },
  ]

  const handleExport = async () => {
    try {
      setLoading(true)

      const params = {}
      if (dateRange?.length === 2) {
        params.start_date = dayjs(dateRange[0]).format("YYYY-MM-DD")
        params.end_date = dayjs(dateRange[1]).format("YYYY-MM-DD")
      }
      if (selectedUsers?.length > 0) {
        params.employee_names = selectedUsers.join(",")
      }
      params.userId = user?.id;
      params.status = activeTab;
      params.language = language

      const response = await axios.get(GET.GET_LEAVE_PDF, {
        params: params,
        responseType: "blob",
      })

      const contentType = response.headers["content-type"]
      if (contentType !== "application/pdf") {
        const reader = new FileReader()
        reader.onload = () => {
          message.error("Server returned non-PDF response: " + reader.result, 5)
        }
        reader.readAsText(response.data)
        return
      }

      const blob = new Blob([response.data], { type: "application/pdf" })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `Leaves_Report_${dayjs().format("YYYY-MM-DD")}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      message.success(translations[language]?.leaveReportDownload, 3)
    } catch (error) {
      console.error("PDF Export Error:", error)
      message.error(translations[language]?.leaveReportDownloadFailed, 5)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    socket.on("leaves:changed", (payload) => {
      console.log("Realtime payload:", payload);
      if (user?.role === "user") {
        if (payload?.user_id === user?.id) {
          fetchLeavesWithioutLoading(dateRange, selectedUsers)
        }
      } else {
        fetchLeavesWithioutLoading(dateRange, selectedUsers)
      }
    });

    return () => {
      socket.off("leaves:changed");
    };
  }, []);
  return (
    <div className="home-container" style={{ padding: 24 }}>
      <div className="leave-management-wrapper">
        <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: '10px'
          }}
        >
          <div className="leave-management-header">
            <h1 className="header-title">{translations[language]?.leaveManagement}</h1>
            <p className="header-subtitle">{translations[language]?.leaveManagementSubtitle}</p>
          </div>
          <div style={{ display: "flex", gap: 10 }} className="filters-container">
            <div>

              <RangePicker
                className="user-select"
                value={dateRange}
                onChange={(dates) => setDateRange(dates)}
                style={{ width: 260, borderRadius: 6, height: 40 }}
              />
            </div>
            <div className="user-filter">

              <Select
                mode="multiple"
                className="user-select leave-management-user-select"
                showSearch
                placeholder={translations[language]?.filterByEmployees}
                allowClear
                style={{ width: 260 }}
                value={selectedUsers}
                onChange={(value) => setSelectedUsers(value)}
              >
                {employeeNameList?.map((emp) => (
                  <Option key={emp?.id} value={emp?.user_name}>
                    {emp?.user_name}
                  </Option>
                ))}
              </Select>
            </div>

          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 16,
            marginBottom: 32,
          }}
        >
          {dashboardCards?.map((card, index) => (
            <div key={index} className="dashboard-card">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                {/* {card.icon}
                <TrendIndicator trend={card.trend.value} isPositive={card.trend.isPositive} /> */}
              </div>
              <h3 style={{ margin: "8px 0", fontSize: "15px" }}>{card.title}</h3>
              <p style={{ fontSize: 18, fontWeight: 600 }}>{card.value}</p>
              <div style={{ marginTop: 8 }}>
                <span style={{ fontSize: 12, color: "#999" }}>{card.period}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="tabs-container" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div className="tabs-header tabs-two-header">
            {[
              { key: "pending", label: translations[language]?.pending },
              { key: "approved", label: translations[language]?.approved },
              { key: "rejected", label: translations[language]?.rejected },
            ]?.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`tab-button ${activeTab === tab.key ? "tab-active" : ""}`}
              >
                {tab.label} ({leaves.filter((l) => l.is_approved === tab.key).length})
              </button>
            ))}
          </div>
          <div>
            <Button
              className="export-btn"
              type={leaves.length === 0 ? "default" : "primary"}
              icon={<FilePdfOutlined />}
              loading={loading}
              style={{ marginRight: '15px' }}
              disabled={leaves.length === 0}
              onClick={handleExport}
            >
              {translations[language].exportPdf}
            </Button>
          </div>
        </div>

        <div className="cards-container">
          {loading ? (
            <div className="empty-state">
              <div className="empty-title">{translations[language]?.loading}</div>
            </div>
          ) : filteredLeaves.length === 0 ? (
            <div className="empty-state">
              <div className="empty-title">
                {translations[language]?.noRequests.replace("{tab}", activeTab)}
              </div>
              <p className="empty-subtitle">
                {translations[language]?.allCaughtUp.replace("{tab}", activeTab)}
              </p>
            </div>
          ) : (
            <div className="cards-list">
              {filteredLeaves.map((leave) => {
                const isExpanded = expandedRows[leave.id]
                const shortReason =
                  leave.reason?.length > 100 ? leave.reason.substring(0, 100) + "..." : leave.reason

                return (
                  <div key={leave?.id} className="leave-manegement-card">
                    <div className="card-content">
                      <div className="card-main">
                        <div className="employee-header">
                          <h3 className="employee-name">{leave?.employee_name}</h3>
                          <span className={`badge ${getTypeClass(leave.type)}`}>{translations[language]?.[leave.type]}</span>
                          <span className={`badge ${getStatusClass(leave.is_approved)}`}>
                            {translations[language]?.[leave.is_approved]?.toUpperCase() || leave.is_approved.toUpperCase()}
                          </span>
                        </div>

                        <div className="details-grid-management">
                          <div className="detail-item-management">
                            <span className="detail-label-management">{translations[language]?.dates}</span>
                            <p className="detail-value-management">
                              {leave.type === "vacation" ? (
                                `${formatDate(leave.start_date)} - ${formatDate(leave.end_date)}`
                              ) : (
                                (() => {
                                  let base = formatDate(leave.leave_date)

                                  if (!leave.full_day && leave.start_time && leave.end_time) {
                                    try {
                                      const startDateTime = `${leave.leave_date} ${leave.start_time}`
                                      const endDateTime = `${leave.leave_date} ${leave.end_time}`
                                      const startLocal = convertItalianToLocalTime(startDateTime)
                                      const endLocal = convertItalianToLocalTime(endDateTime)
                                      const formattedStart = moment(startLocal).format("HH:mm")
                                      const formattedEnd = moment(endLocal).format("HH:mm")

                                      base += ` (${formattedStart} - ${formattedEnd})`
                                    } catch (err) {
                                      console.error("Time conversion error:", err)
                                      base += ` (${leave.start_time} - ${leave.end_time})`
                                    }
                                  } else {
                                    base += ` ${translations[language]?.fullDay}`
                                  }

                                  return base
                                })()
                              )}
                            </p>
                          </div>
                          <div className="detail-item-management">
                            <span className="detail-label-management">{translations[language]?.appliedOn}</span>
                            <p className="detail-value-management">{formatDate(leave.created_at)}</p>
                          </div>
                        </div>

                        <div className="reason-section">
                          <span className="detail-label">{translations[language]?.reason}</span>
                          <p className="reason-text">
                            {isExpanded ? leave.reason : shortReason}
                            {leave.reason?.length > 100 && (
                              <button onClick={() => toggleReason(leave.id)} className="show-more-btn">
                                {isExpanded ? translations[language]?.showLess : translations[language]?.showMore}
                              </button>
                            )}
                          </p>
                        </div>
                      </div>

                      {leave?.is_approved === "pending" && (
                        <div className="action-buttons">
                          <button
                            onClick={() =>
                              setShowConfirmModal({ id: leave.id, action: "approved", name: leave.employee_name })
                            }
                            className="approve-btn"
                          >
                            {translations[language]?.approve}
                          </button>
                          <button
                            onClick={() =>
                              setShowConfirmModal({ id: leave.id, action: "rejected", name: leave.employee_name })
                            }
                            className="reject-btn"
                          >
                            {translations[language]?.reject}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {showConfirmModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3 className="modal-title">
                {showConfirmModal.action === "approved"
                  ? translations[language]?.approve
                  : translations[language]?.reject}{" "}
                {translations[language]?.leaveRequest}
              </h3>
              <p className="modal-text">
                {translations[language]?.confirmLeaveAction
                  .replace("{action}", showConfirmModal.action === "approved" ? translations[language]?.approve : translations[language]?.reject)
                  .replace("{name}", showConfirmModal.name)}
              </p>
              <div className="modal-actions">
                <button onClick={() => setShowConfirmModal(null)} className="cancel-btn">
                  {translations[language]?.cancel}
                </button>
                <button
                  onClick={() => handleUpdateStatus(showConfirmModal.id, showConfirmModal.action)}
                  className={`confirm-btn ${showConfirmModal.action === "approved" ? "approve" : "reject"}`}
                >
                  {showConfirmModal.action === "approved" ? translations[language]?.approve : translations[language]?.reject}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default LeaveManagement